import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Building2, Home, Thermometer, Hammer, Layers, Beef, Sparkles, ArrowRight } from 'lucide-react';
import { COMPANY, SERVICES } from '../mock/data';
import { useT } from '../i18n/LanguageContext';
import { SERVICE_TRANSLATIONS } from '../i18n/translations';
import LanguageSwitcher from './LanguageSwitcher';

const iconMap = { Building2, Home, Thermometer, Hammer, Layers, Beef, Sparkles };
const accentMap = {
  kundenmaurer: 'bg-brand-blue',
  plattenleger: 'bg-brand-blueDark',
  hardrock: 'bg-brand-blueLight',
  abhof: 'bg-brand-blueDeep',
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, lang } = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { to: '/', label: t('nav.home') },
    { to: '/dienstleistungen', label: t('nav.services'), hasDropdown: true },
    { to: '/referenzen', label: t('nav.references') },
    { to: '/team', label: t('nav.team') },
    { to: '/ueber-uns', label: t('nav.about') },
    { to: '/kontakt', label: t('nav.contact') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md border-b border-neutral-200'
          : 'bg-white/90 backdrop-blur-sm border-b border-neutral-200/60'
      }`}
    >
      <div className="h-0.5 w-full bg-gradient-to-r from-brand-red via-brand-yellow to-brand-blue"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-32 md:h-40">
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
            <img
              src={COMPANY.logo}
              alt="Frachigers Handwerk Logo"
              className="h-32 w-32 md:h-40 md:w-40 lg:h-44 lg:w-44 object-contain group-hover:scale-110 transition-transform duration-300"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {navItems.map((item) => {
              if (item.hasDropdown) {
                return (
                  <div
                    key={item.to}
                    className="relative"
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `flex items-center gap-1 px-3 py-2 text-xs font-bold tracking-wider uppercase transition-colors whitespace-nowrap ${
                          isActive ? 'text-brand-blue' : 'text-neutral-700 hover:text-brand-blue'
                        }`
                      }
                    >
                      {item.label}
                      <ChevronDown className="w-3.5 h-3.5" />
                    </NavLink>
                    {dropdownOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-0 w-80 bg-white border border-neutral-200 shadow-2xl rounded-md overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="h-0.5 w-full bg-gradient-to-r from-brand-red via-brand-yellow to-brand-blue"></div>
                        {SERVICES.map((s) => {
                          const Icon = iconMap[s.icon] || Building2;
                          const st = SERVICE_TRANSLATIONS[s.id]?.[lang] || {};
                          return (
                            <Link
                              key={s.id}
                              to={`/dienstleistungen/${s.slug}`}
                              onClick={() => setDropdownOpen(false)}
                              className="group flex items-center gap-4 px-5 py-4 hover:bg-neutral-50 transition-colors border-b border-neutral-100 last:border-0"
                            >
                              <div className={`w-11 h-11 rounded-md ${accentMap[s.id]} text-white flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform`}>
                                <Icon className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold text-neutral-900 group-hover:text-brand-blue transition-colors">{st.title}</div>
                                <div className="text-[11px] text-neutral-500 truncate">{st.short}</div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-brand-blue group-hover:translate-x-1 transition-all" />
                            </Link>
                          );
                        })}
                        <Link
                          to="/dienstleistungen"
                          onClick={() => setDropdownOpen(false)}
                          className="block px-5 py-3 text-center text-xs uppercase tracking-wider font-bold text-brand-blue hover:bg-neutral-50 border-t border-neutral-200"
                        >
                          {t('services.allBtn')} →
                        </Link>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `px-3 py-2 text-xs font-bold tracking-wider uppercase transition-colors whitespace-nowrap ${
                      isActive ? 'text-brand-blue' : 'text-neutral-700 hover:text-brand-blue'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
            <div className="ml-2 pl-3 border-l border-neutral-200 flex items-center gap-2">
              <LanguageSwitcher variant="dark" />
              <a
                href={`tel:${COMPANY.phoneInternational}`}
                className="flex items-center gap-2 bg-brand-blue hover:bg-brand-blueDark text-white px-4 py-2.5 text-xs font-bold tracking-wider uppercase rounded-sm shadow-md whitespace-nowrap"
                style={{ animation: 'btnPulse 2.4s infinite' }}
              >
                <Phone className="w-3.5 h-3.5" />
                {COMPANY.phone}
              </a>
            </div>
          </nav>

          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher variant="dark" />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="text-neutral-900 p-2"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-neutral-200 py-4">
            {navItems.map((item) => (
              <div key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `block py-3 px-2 text-sm uppercase tracking-wide font-bold ${
                      isActive ? 'text-brand-blue' : 'text-neutral-800'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
                {item.hasDropdown && (
                  <div className="pl-4 pb-2 space-y-2">
                    {SERVICES.map((s) => {
                      const Icon = iconMap[s.icon] || Building2;
                      const st = SERVICE_TRANSLATIONS[s.id]?.[lang] || {};
                      return (
                        <Link
                          key={s.id}
                          to={`/dienstleistungen/${s.slug}`}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 py-2 text-sm text-neutral-700 hover:text-brand-blue"
                        >
                          <div className={`w-9 h-9 rounded ${accentMap[s.id]} text-white flex items-center justify-center`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          {st.title}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            <a
              href={`tel:${COMPANY.phoneInternational}`}
              className="mt-4 flex items-center justify-center gap-2 bg-brand-blue text-white px-5 py-3 text-sm font-bold uppercase rounded-sm"
              style={{ animation: 'btnPulse 2.4s infinite' }}
            >
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
